import { addDays } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * The next free instant inside the workspace's best posting windows.
 *
 * `get_best_time` reports each window as a weekday + hour in the WORKSPACE's
 * zone (`brand_kits.timezone`, migration 20260926005000), not the viewer's. A
 * team member in another zone resolving "Thursday 10" in their browser clock
 * would land hours away from the window the data describes, so the wall clock
 * is built in the workspace zone and converted to an instant here.
 */

/** Mirrors `public.posting_timezone`: the zone the posting hours were recorded in. */
export function postingTimezone(
  brandKitZone: string | null | undefined,
  userZone: string | null | undefined,
): string {
  if (isValidZone(brandKitZone)) return brandKitZone;
  if (isValidZone(userZone)) return userZone;
  return 'America/New_York';
}

const DAY_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export interface BestWindow {
  dow: string;
  hour: number;
}

export interface SuggestedSlotOptions {
  /** Do not suggest anything sooner than this many minutes from now. */
  leadMinutes?: number;
  /** A taken post within this many minutes of a window blocks it. */
  spacingMinutes?: number;
  /** How far ahead to look, in days. */
  horizonDays?: number;
}

function isValidZone(zone: string | null | undefined): zone is string {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function resolveSuggestedSlot(
  windows: readonly BestWindow[],
  timezone: string | null | undefined,
  taken: readonly Date[],
  now: Date,
  options: SuggestedSlotOptions = {},
): Date | null {
  const zone = isValidZone(timezone) ? timezone : 'UTC';
  const lead = (options.leadMinutes ?? 30) * 60_000;
  const spacing = (options.spacingMinutes ?? 60) * 60_000;
  const horizon = options.horizonDays ?? 14;

  const parsed = windows
    .map((w) => ({ day: DAY_INDEX[w.dow.trim().toLowerCase()], hour: w.hour }))
    .filter(
      (w): w is { day: number; hour: number } =>
        w.day !== undefined && Number.isInteger(w.hour) && w.hour >= 0 && w.hour <= 23,
    );
  if (parsed.length === 0) return null;

  const localToday = toZonedTime(now, zone);
  const candidates: Date[] = [];
  for (let offset = 0; offset <= horizon; offset++) {
    const localDay = addDays(localToday, offset);
    for (const w of parsed) {
      if (localDay.getDay() !== w.day) continue;
      const wallClock = new Date(
        localDay.getFullYear(),
        localDay.getMonth(),
        localDay.getDate(),
        w.hour,
        0,
        0,
        0,
      );
      candidates.push(fromZonedTime(wallClock, zone));
    }
  }

  return (
    candidates
      .sort((a, b) => a.getTime() - b.getTime())
      .find(
        (c) =>
          c.getTime() >= now.getTime() + lead &&
          !taken.some((t) => Math.abs(t.getTime() - c.getTime()) < spacing),
      ) ?? null
  );
}
